// src/pages/AdminUsers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  ExternalLink,
  Infinity as InfinityIcon,
  Lock,
  Unlock,
  Clock,
  RotateCcw,
  X,
  LayoutPanelTop,
  Gift,
} from "lucide-react";
import AdminAlertsPanel from "../components/AdminAlertsPanel";

/** Brand */
const BG = "#0C0C1A";
const CARD = "#141428";
const BORDER = "#2A2A40";

/** API */
const API_BASE =
  "https://ecom-ads-ai-production.up.railway.app";

/** FE defaults for "Reset" (do not persist unless you click Save) */
const DEFAULT_GENERATED_ASSETS_RETENTION_FE = {
  enabled: true,
  retention_days: 30,
  frequency: "daily",
  last_run_at: null,
  last_result: null,
  running: false,
};

const DEFAULT_TOPVIEW_QUEUE_FE = {
  tick_ms: 3000,
  batch: 1,
  per_api_limit: 1,
};

const RETENTION_FREQUENCY_OPTIONS = [
  { value: "hourly", label: "Every hour" },
  { value: "every_6_hours", label: "Every 6 hours" },
  { value: "every_12_hours", label: "Every 12 hours" },
  { value: "daily", label: "Every day" },
];

function retentionFrequencyLabel(value) {
  return (
    RETENTION_FREQUENCY_OPTIONS.find((x) => x.value === value)?.label ||
    "Every day"
  );
}

const DEFAULT_TIER_CAPS_FE = {
  starter: {
    images: 20,
    videos: 4,
    urls: 10,
    price_monthly_eur: 19.99,
    price_yearly_eur: 199.9,
    // ✅ NEW: Stripe IDs (optional)
    stripe_price_id_monthly: "",
    stripe_price_id_yearly: "",
  },
  pro: {
    images: 70,
    videos: 10,
    urls: 30,
    price_monthly_eur: 39.99,
    price_yearly_eur: 399.9,
    stripe_price_id_monthly: "",
    stripe_price_id_yearly: "",
  },
  business: {
    images: 200,
    videos: 22,
    urls: 80,
    price_monthly_eur: 59.99,
    price_yearly_eur: 599.9,
    stripe_price_id_monthly: "",
    stripe_price_id_yearly: "",
  },

  // ✅ Signup bonus / trial offer (one-time)
  signup_bonus: {
    images: 5,
    videos: 1,
    urls: 1,
  },

  addons: {
    images_10: { amount: 10, price_eur: 8, stripe_price_id: "" },
    video_1: { amount: 1, price_eur: 6, stripe_price_id: "" },
    url_1: { amount: 1, price_eur: 4, stripe_price_id: "" },
  },
};

const Chip = ({ children, kind = "default", size = "xs" }) => {
  const map = {
    default: "bg-zinc-800/70 text-zinc-200 border border-zinc-700/60",
    green: "bg-emerald-600/15 text-emerald-300 border-emerald-700/40",
    red: "bg-rose-600/15 text-rose-300 border-rose-700/40",
    accent: "bg-pink-600/15 text-pink-300 border-pink-700/40",
    yellow: "bg-amber-600/15 text-amber-300 border-amber-700/40",
  };
  const sizeClass =
    size === "lg"
      ? "text-base sm:text-lg"
      : size === "sm"
      ? "text-sm"
      : "text-xs";
  return (
    <span
      className={`px-2.5 py-1 rounded-full ${sizeClass} whitespace-nowrap ${
        map[kind] || map.default
      }`}
    >
      {children}
    </span>
  );
};

const Header = ({ children }) => (
  <div
    className="shrink-0 flex flex-col gap-3 px-4 py-3"
    style={{ background: BG, borderBottom: `1px solid ${BORDER}` }}
  >
    {children}
  </div>
);

/** Avatar (matches detail page) */
const Avatar = ({ name, email }) => {
  const initial = (name || email || "U").slice(0, 1).toUpperCase();
  return (
    <div className="w-7 h-7 md:w-8 md:h-8 rounded-md bg-gradient-to-br from-[#FC2783] to-[#9C27B0] flex items-center justify-center text-white font-bold text-xs md:text-sm select-none shrink-0">
      {initial}
    </div>
  );
};

/** Status → chip tone */
const statusTone = (s) => {
  if (!s) return "default";
  const v = String(s).toLowerCase();
  if (v === "active" || v === "trialing") return "green";
  if (v === "past_due" || v === "unpaid") return "yellow";
  if (v === "canceled" || v === "incomplete_expired") return "red";
  return "default";
};

const intervalLabel = (i) =>
  i === "year" ? "annual" : i === "month" ? "monthly" : null;

const nextPeriodLabel = (cancelAtEnd, ts, planInterval) => {
  if (!ts) return null;
  const date = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
  const when = date.toLocaleDateString();
  const il = intervalLabel(planInterval);
  return cancelAtEnd
    ? `Cancels on ${when}`
    : `Renews on ${when}${il ? ` · ${il}` : ""}`;
};

function addMonthsSafe(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() !== day) d.setDate(0);
  return d;
}

const fmtShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

const fmtMaybeEpochOrIso = (v) => {
  if (!v) return "—";
  if (typeof v === "number") return new Date(v * 1000).toLocaleDateString();
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");

  // action loading states (per user)
  const [busyUnlimited, setBusyUnlimited] = useState(new Set());
  const [busyBlocked, setBusyBlocked] = useState(new Set());

  // ✅ add-ons modal state
  const [addonsOpen, setAddonsOpen] = useState(false);
  const [addonsUser, setAddonsUser] = useState(null);
  const [addonsErr, setAddonsErr] = useState("");
  const [addonsForm, setAddonsForm] = useState({
    images: 5,
    videos: 5,
    urls: 5,
  });
  const [busyAddons, setBusyAddons] = useState(new Set());

  // time travel modal state
  const [ttOpen, setTTOpen] = useState(false);
  const [ttUser, setTTUser] = useState(null);
  const [ttMonths, setTTMonths] = useState(0);
  const [ttErr, setTTErr] = useState("");
  const [busyTimeTravel, setBusyTimeTravel] = useState(new Set());

  // global revenue totals (EUR)
  const [globalTotals, setGlobalTotals] = useState({
    tiers_eur: 0,
    addons_eur: 0,
    total_eur: 0,
  });
  const euro = (n) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(n || 0);

  // query state
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [sort, setSort] = useState("last_seen_at:desc");
  const [paying, setPaying] = useState("");
  const [blocked, setBlocked] = useState("");
  const [unlimited, setUnlimited] = useState("");
  const [tier, setTier] = useState("");
  const [canceled, setCanceled] = useState("");

  // ✅ column-by-column filters (single input per column; no min/max)
  const [colFilters, setColFilters] = useState({
    paidMonths: "",
    images: "",
    videos: "",
    urls: "",
    revenue: "",
    lastSeenAfter: "",
    lastSeenBefore: "",
    periodAfter: "",
    periodBefore: "",
    renewalType: "",
  });

  const debouncedQ = useMemo(() => q, [q]);

  const paidMonthsLabel = (r) => {
    const paid = Number(r?.paid_months_count ?? 0);
    const totalMonths = Math.max(1, Number(r?.months_since_signup ?? 1));
    const pct = Math.round((paid / totalMonths) * 100);
    return `${paid}/${totalMonths} (${pct}%)`;
  };

  const setFilter = (key, value) => {
    setColFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearColumnFilters = () => {
    setColFilters({
      paidMonths: "",
      images: "",
      videos: "",
      urls: "",
      revenue: "",
      lastSeenAfter: "",
      lastSeenBefore: "",
      periodAfter: "",
      periodBefore: "",
      renewalType: "",
    });
  };

  async function fetchPage(p) {
    setLoading(true);
    setErr("");
    try {
      const token = await getToken();
      if (!token) {
        setErr("Auth token missing. Are you signed in with Clerk?");
        if (p === 1) setRows([]);
        setTotal(0);
        return;
      }

      const params = new URLSearchParams();
      if (debouncedQ) params.set("search", debouncedQ);
      params.set("page", String(p));
      params.set("limit", String(limit));
      if (sort) params.set("sort", sort);
      if (paying) params.set("paying", paying);
      if (blocked) params.set("blocked", blocked);
      if (unlimited) params.set("unlimited", unlimited);
      if (tier) params.set("tier", tier);
      if (canceled) params.set("canceled", canceled);

      // ✅ column filters → backend params (use *_min only)
      if (colFilters.paidMonths)
        params.set("paid_months_min", colFilters.paidMonths);
      if (colFilters.images) params.set("images_min", colFilters.images);
      if (colFilters.videos) params.set("videos_min", colFilters.videos);
      if (colFilters.urls) params.set("urls_min", colFilters.urls);
      if (colFilters.revenue) params.set("revenue_min", colFilters.revenue);

      if (colFilters.lastSeenAfter)
        params.set("last_seen_after", colFilters.lastSeenAfter);
      if (colFilters.lastSeenBefore)
        params.set("last_seen_before", colFilters.lastSeenBefore);

      if (colFilters.periodAfter)
        params.set("period_end_after", colFilters.periodAfter);
      if (colFilters.periodBefore)
        params.set("period_end_before", colFilters.periodBefore);

      if (colFilters.renewalType)
        params.set("renewal_type", colFilters.renewalType);

      const url = `${API_BASE}/api/admin/users?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const text = await res.text();
        // eslint-disable-next-line no-console
        console.error(
          "Non-JSON response from /api/admin/users:",
          text.slice(0, 200)
        );
        throw new Error("Unexpected non-JSON response from API.");
      }

      const json = await res.json();
      if (!res.ok) {
        setErr(json?.error || "Failed to load users");
        if (p === 1) setRows([]);
        setTotal(0);
        return;
      }

      const incoming = Array.isArray(json.data) ? json.data : [];
      setTotal(json.total || 0);
      setRows((prev) => (p > 1 ? [...prev, ...incoming] : incoming));

      if (json?.totals) setGlobalTotals(json.totals);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      if (page === 1) setRows([]);
      setTotal(0);
      setErr("Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedQ,
    paying,
    blocked,
    unlimited,
    tier,
    canceled,
    sort,
    colFilters.paidMonths,
    colFilters.images,
    colFilters.videos,
    colFilters.urls,
    colFilters.revenue,
    colFilters.lastSeenAfter,
    colFilters.lastSeenBefore,
    colFilters.periodAfter,
    colFilters.periodBefore,
    colFilters.renewalType,
  ]);

  useEffect(() => {
    fetchPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    debouncedQ,
    paying,
    blocked,
    unlimited,
    tier,
    canceled,
    sort,
    colFilters.paidMonths,
    colFilters.images,
    colFilters.videos,
    colFilters.urls,
    colFilters.revenue,
    colFilters.lastSeenAfter,
    colFilters.lastSeenBefore,
    colFilters.periodAfter,
    colFilters.periodBefore,
    colFilters.renewalType,
  ]);

  // Infinite scroll sentinel
  useEffect(() => {
    const el = document.getElementById("infinite-sentinel");
    if (!el) return;
    const onIntersect = (entries) => {
      const hasMore = rows.length < total;
      if (entries[0].isIntersecting && !loading && hasMore) {
        setPage((pp) => pp + 1);
      }
    };
    const io = new IntersectionObserver(onIntersect, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [rows.length, total, loading]);

  const SortBtn = ({ col, label }) => {
    const [c, d] = sort.includes(":") ? sort.split(":") : [sort, "desc"];
    const active = c === col;
    const dir = active ? d : "desc";

    const onClick = () => {
      const next = active ? (dir === "asc" ? "desc" : "asc") : "desc";
      setSort(`${col}:${next}`);
    };

    return (
      <button
        onClick={onClick}
        className={`flex items-center gap-1 ${
          active ? "text-zinc-100" : "text-zinc-400"
        } hover:text-zinc-100`}
      >
        <span>{label}</span>
        {active ? (
          dir === "asc" ? (
            <ChevronUp size={14} />
          ) : (
            <ChevronDown size={14} />
          )
        ) : null}
      </button>
    );
  };

  const isBillingInactive = (r) => {
    const s = String(r?.sub_status || "").toLowerCase();
    return (
      (!r?.current_tier && (s === "canceled" || s === "incomplete_expired")) ||
      (!r?.is_paying && !r?.current_tier)
    );
  };

  const isNeverPaid = (r) =>
    (r?.revenue_total_cents || 0) === 0 && !r?.is_paying;

  const renewalOrCancelCell = (r) => {
    const lbl = nextPeriodLabel(
      r.cancel_at_period_end,
      r.current_period_end,
      r.plan_interval
    );
    return lbl || "—";
  };

  const revenueCell = (r) => {
    const totalEur = (Number(r.revenue_total_cents || 0) || 0) / 100;
    const tiersEur = (Number(r.revenue_tiers_cents || 0) || 0) / 100;
    const addonsEur = (Number(r.revenue_addons_cents || 0) || 0) / 100;

    return (
      <div className="flex flex-col">
        <span className="text-zinc-100 font-medium">{euro(totalEur)}</span>
        <span className="text-[11px] text-zinc-400">
          tiers {euro(tiersEur)} · add-ons {euro(addonsEur)}
        </span>
      </div>
    );
  };

  const setRowPatch = (id, patch) =>
    setRows((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const toggleUnlimited = async (userId, nextVal) => {
    const token = await getToken();
    const s = new Set(busyUnlimited);
    s.add(userId);
    setBusyUnlimited(s);
    const revert = rows.find((r) => r.id === userId)?.has_unlimited ?? false;

    setRowPatch(userId, { has_unlimited: nextVal });

    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users/${encodeURIComponent(
          userId
        )}/unlimited`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ enabled: nextVal }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to toggle unlimited");
      if (json?.user?.has_unlimited !== undefined) {
        setRowPatch(userId, { has_unlimited: !!json.user.has_unlimited });
      }
    } catch (e) {
      setErr(e.message || "Failed to toggle unlimited");
      setRowPatch(userId, { has_unlimited: revert });
    } finally {
      const s2 = new Set(busyUnlimited);
      s2.delete(userId);
      setBusyUnlimited(s2);
    }
  };

  const toggleBlocked = async (userId, nextVal) => {
    const token = await getToken();
    const s = new Set(busyBlocked);
    s.add(userId);
    setBusyBlocked(s);
    const revert = rows.find((r) => r.id === userId)?.is_blocked ?? false;

    setRowPatch(userId, { is_blocked: nextVal });

    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/block`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ blocked: nextVal }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to toggle block");
      if (json?.user?.is_blocked !== undefined) {
        setRowPatch(userId, { is_blocked: !!json.user.is_blocked });
      }
    } catch (e) {
      setErr(e.message || "Failed to toggle block");
      setRowPatch(userId, { is_blocked: revert });
    } finally {
      const s2 = new Set(busyBlocked);
      s2.delete(userId);
      setBusyBlocked(s2);
    }
  };

  // ✅ add-ons helpers
  const parseNonNegInt = (val) => {
    const n = Number.parseInt(String(val).replace(/[^\d]/g, ""), 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  // ✅ money parser for EUR inputs (supports "9,99" or "9.99" or "1.000,50")
  const parseNonNegMoney = (val) => {
    const s = String(val ?? "").trim();
    if (!s) return 0;
    const clean = s.replace(/[^\d.,]/g, "");
    if (!clean) return 0;

    const num =
      clean.includes(".") && clean.includes(",")
        ? Number.parseFloat(clean.replace(/\./g, "").replace(",", "."))
        : clean.includes(",")
        ? Number.parseFloat(clean.replace(",", "."))
        : Number.parseFloat(clean);

    if (!Number.isFinite(num) || num < 0) return 0;
    return Math.round(num * 100) / 100;
  };

  const openAddons = (row) => {
    setAddonsUser(row);
    setAddonsErr("");
    setAddonsForm({ images: 5, videos: 5, urls: 5 });
    setAddonsOpen(true);
  };

  const closeAddons = () => {
    setAddonsOpen(false);
    setAddonsUser(null);
    setAddonsErr("");
    setAddonsForm({ images: 5, videos: 5, urls: 5 });
  };

  const applyAddons = async (userId, mode) => {
    const token = await getToken();
    if (!token) {
      setAddonsErr("Missing auth token.");
      return;
    }

    const imgs = parseNonNegInt(addonsForm.images);
    const vids = parseNonNegInt(addonsForm.videos);
    const urls = parseNonNegInt(addonsForm.urls);

    if (imgs === 0 && vids === 0 && urls === 0) {
      setAddonsErr("Please enter at least one amount.");
      return;
    }

    const s = new Set(busyAddons);
    s.add(userId);
    setBusyAddons(s);
    setAddonsErr("");

    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/addons`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            mode,
            images: imgs,
            videos: vids,
            urls,
          }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to update add-ons");

      const next = json?.addon_counts || json?.caps || null;
      if (!next) throw new Error("API did not return addon_counts.");

      const nextImages = Number(next.images_extra ?? next.images ?? 0) || 0;
      const nextVideos = Number(next.videos_extra ?? next.videos ?? 0) || 0;
      const nextUrls = Number(next.urls_extra ?? next.urls ?? 0) || 0;

      const row = rows.find((r) => r.id === userId);
      const tierCaps = row?.tier_caps || { images: 0, videos: 0, urls: 0 };
      const used = row?.used_in_cycle || {
        used_images: 0,
        used_videos: 0,
        used_urls: 0,
      };

      const totalCaps = {
        images: Number(tierCaps.images || 0) + nextImages,
        videos: Number(tierCaps.videos || 0) + nextVideos,
        urls: Number(tierCaps.urls || 0) + nextUrls,
      };

      const remaining =
        row?.has_unlimited
          ? { images: null, videos: null, urls: null }
          : {
              images: Math.max(
                0,
                totalCaps.images - (Number(used.used_images) || 0)
              ),
              videos: Math.max(
                0,
                totalCaps.videos - (Number(used.used_videos) || 0)
              ),
              urls: Math.max(
                0,
                totalCaps.urls - (Number(used.used_urls) || 0)
              ),
            };

      setRowPatch(userId, {
        addon_image_count: nextImages,
        addon_video_count: nextVideos,
        addon_url_count: nextUrls,
        total_caps: totalCaps,
        remaining_in_cycle: remaining,
      });

      // also update current modal "user snapshot"
      if (addonsUser?.id === userId) {
        setAddonsUser((prev) =>
          prev
            ? {
                ...prev,
                addon_image_count: nextImages,
                addon_video_count: nextVideos,
                addon_url_count: nextUrls,
                total_caps: totalCaps,
                remaining_in_cycle: remaining,
              }
            : prev
        );
      }
    } catch (e) {
      setAddonsErr(e?.message || "Failed to update add-ons");
    } finally {
      const s2 = new Set(busyAddons);
      s2.delete(userId);
      setBusyAddons(s2);
    }
  };

  // time travel helpers
  const openTimeTravel = (row) => {
    setTTUser(row);
    setTTMonths(Number(row?.time_travel_months || 0));
    setTTErr("");
    setTTOpen(true);
  };

  const closeTimeTravel = () => {
    setTTOpen(false);
    setTTUser(null);
    setTTMonths(0);
    setTTErr("");
  };

  const applyTimeTravel = async (userId, months) => {
    const token = await getToken();
    const s = new Set(busyTimeTravel);
    s.add(userId);
    setBusyTimeTravel(s);
    setTTErr("");

    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users/${encodeURIComponent(
          userId
        )}/time-travel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ months }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to apply time travel");

      setPage(1);
      await fetchPage(1);

      closeTimeTravel();
    } catch (e) {
      setTTErr(e.message || "Failed to apply time travel");
    } finally {
      const s2 = new Set(busyTimeTravel);
      s2.delete(userId);
      setBusyTimeTravel(s2);
    }
  };

  /** ===================== Tier Caps + Prices + Stripe IDs Editor (Header) ===================== */
  const [showCaps, setShowCaps] = useState(false);
  const [capsLoading, setCapsLoading] = useState(false);
  const [capsSaving, setCapsSaving] = useState(false);
  const [capsErr, setCapsErr] = useState("");
  const [capsDirty, setCapsDirty] = useState(false);
  const [tierCaps, setTierCaps] = useState(null);

  // ✅ NEW: Stripe price id validation results (bulk lookup)
  const [stripeCheckBusy, setStripeCheckBusy] = useState(false);
  const [stripeCheck, setStripeCheck] = useState({ prices: {}, errors: {} });

  /** ===================== Generated Assets Retention Editor ===================== */
  const [showRetention, setShowRetention] = useState(false);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionRunning, setRetentionRunning] = useState(false);
  const [retentionErr, setRetentionErr] = useState("");
  const [retentionDirty, setRetentionDirty] = useState(false);
  const [retentionCfg, setRetentionCfg] = useState(null);

    /** ===================== TopView Queue Editor ===================== */
    const [showTopviewQueue, setShowTopviewQueue] = useState(false);
    const [topviewQueueLoading, setTopviewQueueLoading] = useState(false);
    const [topviewQueueSaving, setTopviewQueueSaving] = useState(false);
    const [topviewQueueErr, setTopviewQueueErr] = useState("");
    const [topviewQueueDirty, setTopviewQueueDirty] = useState(false);
    const [topviewQueueCfg, setTopviewQueueCfg] = useState(null);
    const [topviewKeys, setTopviewKeys] = useState([]);
const [topviewKeysLoading, setTopviewKeysLoading] = useState(false);
const [topviewKeysErr, setTopviewKeysErr] = useState("");
const [busyTopviewKeySave, setBusyTopviewKeySave] = useState(new Set());

const normalizeTopviewKey = (raw = {}) => ({
  id: raw.id || "",
  label: String(raw.label || ""),
  uid: String(raw.uid || ""),
  api_key: String(raw.api_key || raw.apiKey || ""),
  enabled: raw.enabled !== false,
  monthly_quota:
    raw.monthly_quota == null || raw.monthly_quota === ""
      ? null
      : parseNonNegInt(raw.monthly_quota),
  used_credits: Math.max(
    0,
    parseNonNegInt(raw.used_credits ?? raw.usedThisPeriod ?? 0)
  ),
  masked_api_key: String(raw.masked_api_key || raw.maskedApiKey || ""),
  concurrency_limit: Math.max(
    1,
    parseNonNegInt(
      raw.concurrency_limit ??
        raw.queue_limit ??
        raw.concurrencyLimit ??
        raw.queueLimit ??
        1
    )
  ),
  active_jobs: Math.max(0, parseNonNegInt(raw.active_jobs ?? 0)),
  runtime_enabled:
    raw.runtime_enabled === undefined ? true : raw.runtime_enabled !== false,
  health_status: String(raw.health_status || "healthy"),
  runtime_last_error: raw.runtime_last_error || null,
  last_used_at: raw.last_used_at || raw.lastUsedAt || null,
  last_error: raw.last_error || raw.lastError || null,
  last_claimed_at: raw.last_claimed_at || null,
  last_released_at: raw.last_released_at || null,
  last_heartbeat_at: raw.last_heartbeat_at || null,
});

const loadTopviewKeys = async () => {
  setTopviewKeysLoading(true);
  setTopviewKeysErr("");
  try {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/api/admin/topview-keys`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.error || "Failed to load TopView keys");
    }

    const incoming = Array.isArray(json?.keys) ? json.keys : [];
    setTopviewKeys(incoming.map(normalizeTopviewKey));
  } catch (e) {
    setTopviewKeysErr(e.message || "Failed to load TopView keys");
    setTopviewKeys([]);
  } finally {
    setTopviewKeysLoading(false);
  }
};

const saveTopviewKeyQueue = async (keyId) => {
  const busy = new Set(busyTopviewKeySave);
  busy.add(keyId);
  setBusyTopviewKeySave(busy);
  setTopviewKeysErr("");

  try {
    const token = await getToken();

    const payload = {
      keys: topviewKeys.map((k) => ({
        id: k.id,
        label: k.label,
        uid: k.uid || "",
        api_key: k.api_key || "",
        enabled: !!k.enabled,
        monthly_quota:
          k.monthly_quota == null || k.monthly_quota === ""
            ? null
            : Math.max(1, parseNonNegInt(k.monthly_quota)),
        used_credits: Math.max(0, parseNonNegInt(k.used_credits || 0)),
        concurrency_limit: Math.max(
          1,
          parseNonNegInt(k.concurrency_limit ?? 1)
        ),
        last_used_at: k.last_used_at || null,
        last_error: k.last_error || null,
      })),
    };
    console.log("saveTopviewKeyQueue API_BASE =", API_BASE);
    console.log("saveTopviewKeyQueue payload =", payload);
    const res = await fetch(`${API_BASE}/api/admin/topview-keys`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.error || "Failed to save TopView key queue");
    }

    const incoming = Array.isArray(json?.keys) ? json.keys : [];
    setTopviewKeys(incoming.map(normalizeTopviewKey));
  } catch (e) {
    setTopviewKeysErr(e.message || "Failed to save TopView key queue");
  } finally {
    const nextBusy = new Set(busyTopviewKeySave);
    nextBusy.delete(keyId);
    setBusyTopviewKeySave(nextBusy);
  }
};

  const tiers = ["starter", "pro", "business"];
  const capFields = ["images", "videos", "urls"];
  const priceFields = ["price_monthly_eur", "price_yearly_eur"];

  const normalizeTierCaps = (incoming) => {
    if (!incoming) return null;

    const pickTier = (t) => ({
      images: parseNonNegInt(
        incoming?.[t]?.images ?? DEFAULT_TIER_CAPS_FE?.[t]?.images ?? 0
      ),
      videos: parseNonNegInt(
        incoming?.[t]?.videos ?? DEFAULT_TIER_CAPS_FE?.[t]?.videos ?? 0
      ),
      urls: parseNonNegInt(
        incoming?.[t]?.urls ?? DEFAULT_TIER_CAPS_FE?.[t]?.urls ?? 0
      ),
      price_monthly_eur: parseNonNegMoney(
        incoming?.[t]?.price_monthly_eur ??
          DEFAULT_TIER_CAPS_FE?.[t]?.price_monthly_eur ??
          0
      ),
      price_yearly_eur: parseNonNegMoney(
        incoming?.[t]?.price_yearly_eur ??
          DEFAULT_TIER_CAPS_FE?.[t]?.price_yearly_eur ??
          0
      ),
      // ✅ NEW: Stripe IDs from BE
      stripe_price_id_monthly: String(
        incoming?.[t]?.stripe_price_id_monthly ??
          DEFAULT_TIER_CAPS_FE?.[t]?.stripe_price_id_monthly ??
          ""
      ),
      stripe_price_id_yearly: String(
        incoming?.[t]?.stripe_price_id_yearly ??
          DEFAULT_TIER_CAPS_FE?.[t]?.stripe_price_id_yearly ??
          ""
      ),
    });

    const addonsIn = incoming?.addons || {};
    const addonsDef = DEFAULT_TIER_CAPS_FE.addons || {};

    // ✅ FIX: backend may store add-on amount under bump.{images|videos|urls}
    const amountFromBump = (key) => {
      const b = addonsIn?.[key]?.bump || {};
      if (key === "images_10") return b.images;
      if (key === "video_1") return b.videos;
      if (key === "url_1") return b.urls;
      return null;
    };

    // ✅ Signup bonus (accept multiple possible backend shapes for safety)
    const sbIn =
      incoming?.signup_bonus ||
      incoming?.signupBonus ||
      incoming?.trial_bonus ||
      incoming?.trial_offer ||
      incoming?.trial_offer_bonus ||
      incoming?.free_trial_bonus ||
      incoming?.free_trial_offer ||
      incoming?.signup_addons ||
      null;

    const signup_bonus = {
      images: parseNonNegInt(
        sbIn?.images ?? DEFAULT_TIER_CAPS_FE?.signup_bonus?.images ?? 0
      ),
      videos: parseNonNegInt(
        sbIn?.videos ?? DEFAULT_TIER_CAPS_FE?.signup_bonus?.videos ?? 0
      ),
      urls: parseNonNegInt(
        sbIn?.urls ?? DEFAULT_TIER_CAPS_FE?.signup_bonus?.urls ?? 0
      ),
    };

    return {
      starter: pickTier("starter"),
      pro: pickTier("pro"),
      business: pickTier("business"),
      signup_bonus,
      addons: {
        images_10: {
          amount: parseNonNegInt(
            addonsIn?.images_10?.amount ??
              amountFromBump("images_10") ??
              addonsDef?.images_10?.amount ??
              0
          ),
          price_eur: parseNonNegMoney(
            addonsIn?.images_10?.price_eur ??
              addonsDef?.images_10?.price_eur ??
              0
          ),
          stripe_price_id: String(
            addonsIn?.images_10?.stripe_price_id ??
              addonsDef?.images_10?.stripe_price_id ??
              ""
          ),
        },
        video_1: {
          amount: parseNonNegInt(
            addonsIn?.video_1?.amount ??
              amountFromBump("video_1") ??
              addonsDef?.video_1?.amount ??
              0
          ),
          price_eur: parseNonNegMoney(
            addonsIn?.video_1?.price_eur ??
              addonsDef?.video_1?.price_eur ??
              0
          ),
          stripe_price_id: String(
            addonsIn?.video_1?.stripe_price_id ??
              addonsDef?.video_1?.stripe_price_id ??
              ""
          ),
        },
        url_1: {
          amount: parseNonNegInt(
            addonsIn?.url_1?.amount ??
              amountFromBump("url_1") ??
              addonsDef?.url_1?.amount ??
              0
          ),
          price_eur: parseNonNegMoney(
            addonsIn?.url_1?.price_eur ?? addonsDef?.url_1?.price_eur ?? 0
          ),
          stripe_price_id: String(
            addonsIn?.url_1?.stripe_price_id ??
              addonsDef?.url_1?.stripe_price_id ??
              ""
          ),
        },
      },
    };
  };

  // ✅ NEW: bulk validate Stripe price ids shown in the editor
  const validateStripePriceIds = async (capsObj) => {
    const caps = capsObj || tierCaps;
    if (!caps) return;

    const ids = [];
    for (const t of tiers) {
      const m = String(caps?.[t]?.stripe_price_id_monthly || "").trim();
      const y = String(caps?.[t]?.stripe_price_id_yearly || "").trim();
      if (m) ids.push(m);
      if (y) ids.push(y);
    }
    const addKeys = ["images_10", "video_1", "url_1"];
    for (const k of addKeys) {
      const pid = String(caps?.addons?.[k]?.stripe_price_id || "").trim();
      if (pid) ids.push(pid);
    }

    const uniq = [...new Set(ids)].slice(0, 50);
    if (uniq.length === 0) {
      setStripeCheck({ prices: {}, errors: {} });
      return;
    }

    setStripeCheckBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/stripe/prices/lookup`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ ids: uniq }),
      });
      const json = await res.json();
      if (!res.ok)
        throw new Error(json?.error || "Failed to lookup Stripe prices");

      setStripeCheck({
        prices: json?.prices || {},
        errors: json?.errors || {},
      });
    } catch (e) {
      setStripeCheck({
        prices: {},
        errors: { _api: e?.message || "Lookup failed" },
      });
    } finally {
      setStripeCheckBusy(false);
    }
  };

  const stripeBadgeForId = (pid, expectedInterval) => {
    const id = String(pid || "").trim();
    if (!id) return null;

    const errMsg = stripeCheck?.errors?.[id];
    const pr = stripeCheck?.prices?.[id];

    if (errMsg) {
      return (
        <div className="mt-1 text-[11px] text-rose-300">
          Invalid: {String(errMsg).slice(0, 120)}
        </div>
      );
    }
    if (!pr)
      return (
        <div className="mt-1 text-[11px] text-zinc-500">Not checked</div>
      );

    const curOk = String(pr.currency || "").toLowerCase() === "eur";
    const interval = pr?.recurring?.interval || null;
    const intervalOk =
      expectedInterval == null
        ? true
        : String(interval || "") === String(expectedInterval);

    const cents = pr.unit_amount ?? null;
    const eurVal =
      Number.isFinite(Number(cents)) && cents != null
        ? (Number(cents) / 100).toFixed(2)
        : null;

    return (
      <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
        <span className={curOk ? "text-emerald-300" : "text-amber-300"}>
          {curOk ? "EUR" : `CUR:${pr.currency || "?"}`}
        </span>
        <span className={intervalOk ? "text-emerald-300" : "text-amber-300"}>
          {interval ? `interval:${interval}` : "one-time"}
        </span>
        {eurVal != null ? <span className="text-zinc-300">{eurVal}€</span> : null}
        {expectedInterval ? (
          <span className="text-zinc-500">(expect {expectedInterval})</span>
        ) : null}
      </div>
    );
  };

  const normalizeGeneratedAssetsRetention = (incoming) => ({
    enabled:
      incoming?.enabled === undefined
        ? DEFAULT_GENERATED_ASSETS_RETENTION_FE.enabled
        : incoming.enabled !== false,
    retention_days: parseNonNegInt(
      incoming?.retention_days ??
        DEFAULT_GENERATED_ASSETS_RETENTION_FE.retention_days
    ),
    frequency:
      String(
        incoming?.frequency ?? DEFAULT_GENERATED_ASSETS_RETENTION_FE.frequency
      ).trim() || DEFAULT_GENERATED_ASSETS_RETENTION_FE.frequency,
    last_run_at: incoming?.last_run_at || null,
    last_result: incoming?.last_result || null,
    running: Boolean(incoming?.running),
  });

  const normalizeTopviewQueue = (incoming) => ({
    tick_ms: parseNonNegInt(
      incoming?.tick_ms ?? incoming?.tickMs ?? DEFAULT_TOPVIEW_QUEUE_FE.tick_ms
    ),
    batch: parseNonNegInt(
      incoming?.batch ??
        incoming?.max_claims_per_tick ??
        incoming?.maxClaimsPerTick ??
        DEFAULT_TOPVIEW_QUEUE_FE.batch
    ),
    per_api_limit: parseNonNegInt(
      incoming?.per_api_limit ??
        incoming?.perApiLimit ??
        incoming?.queue_limit_per_api ??
        DEFAULT_TOPVIEW_QUEUE_FE.per_api_limit
    ),
  });

  const loadTopviewQueue = async () => {
    setTopviewQueueLoading(true);
    setTopviewQueueErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/topview-queue`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || "Failed to load TopView queue settings");
      }
      setTopviewQueueCfg(normalizeTopviewQueue(json?.config || null));
      setTopviewQueueDirty(false);
    } catch (e) {
      setTopviewQueueErr(e.message || "Failed to load TopView queue settings");
      setTopviewQueueCfg(null);
    } finally {
      setTopviewQueueLoading(false);
    }
  };

  const saveTopviewQueue = async () => {
    if (!topviewQueueCfg) return;
    setTopviewQueueSaving(true);
    setTopviewQueueErr("");
    try {
      const token = await getToken();
      const payload = {
        tick_ms: Math.max(1, parseNonNegInt(topviewQueueCfg.tick_ms || 3000)),
        batch: Math.max(1, parseNonNegInt(topviewQueueCfg.batch || 1)),
        per_api_limit: Math.max(
          1,
          parseNonNegInt(topviewQueueCfg.per_api_limit || 1)
        ),
      };

      const res = await fetch(`${API_BASE}/api/admin/topview-queue`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || "Failed to save TopView queue settings");
      }

      setTopviewQueueCfg(normalizeTopviewQueue(json?.config || payload));
      setTopviewQueueDirty(false);
    } catch (e) {
      setTopviewQueueErr(e.message || "Failed to save TopView queue settings");
    } finally {
      setTopviewQueueSaving(false);
    }
  };

  const resetTopviewQueueToDefaults = () => {
    setTopviewQueueCfg({ ...DEFAULT_TOPVIEW_QUEUE_FE });
    setTopviewQueueDirty(true);
  };

  const loadGeneratedAssetsRetention = async () => {
    setRetentionLoading(true);
    setRetentionErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/generated-assets-retention`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || "Failed to load generated assets retention settings");
      }
      setRetentionCfg(normalizeGeneratedAssetsRetention(json?.config || null));
      setRetentionDirty(false);
    } catch (e) {
      setRetentionErr(e.message || "Failed to load generated assets retention settings");
      setRetentionCfg(null);
    } finally {
      setRetentionLoading(false);
    }
  };

  const saveGeneratedAssetsRetention = async () => {
    if (!retentionCfg) return;
    setRetentionSaving(true);
    setRetentionErr("");
    try {
      const token = await getToken();
      const payload = {
        enabled: retentionCfg.enabled !== false,
        retention_days: Math.max(
          0,
          parseNonNegInt(retentionCfg.retention_days)
        ),
        frequency:
          String(
            retentionCfg.frequency ||
              DEFAULT_GENERATED_ASSETS_RETENTION_FE.frequency
          ).trim() || DEFAULT_GENERATED_ASSETS_RETENTION_FE.frequency,
      };
  
      const res = await fetch(`${API_BASE}/api/admin/generated-assets-retention`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(
          json?.error || "Failed to save generated assets retention settings"
        );
      }
      setRetentionCfg(normalizeGeneratedAssetsRetention(json?.config || payload));
      setRetentionDirty(false);
    } catch (e) {
      setRetentionErr(
        e.message || "Failed to save generated assets retention settings"
      );
    } finally {
      setRetentionSaving(false);
    }
  };

  const runGeneratedAssetsRetentionNow = async () => {
    setRetentionRunning(true);
    setRetentionErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/generated-assets-retention/run`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || "Failed to run generated assets retention cleanup");
      }
      await loadGeneratedAssetsRetention();
    } catch (e) {
      setRetentionErr(e.message || "Failed to run generated assets retention cleanup");
    } finally {
      setRetentionRunning(false);
    }
  };

  const resetGeneratedAssetsRetentionToDefaults = () => {
    setRetentionCfg({ ...DEFAULT_GENERATED_ASSETS_RETENTION_FE });
    setRetentionDirty(true);
  };

  const loadTierCaps = async () => {
    setCapsLoading(true);
    setCapsErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/tier-caps`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to load tier caps");

      const incoming = json?.caps || null;
      const normalized = normalizeTierCaps(incoming);
      setTierCaps(normalized);
      setCapsDirty(false);

      // ✅ Optional: auto-validate existing IDs once loaded
      if (normalized) {
        await validateStripePriceIds(normalized);
      }
    } catch (e) {
      setCapsErr(e.message || "Failed to load tier caps");
      setTierCaps(null);
    } finally {
      setCapsLoading(false);
    }
  };

  const saveTierCaps = async () => {
    if (!tierCaps) return;
    setCapsSaving(true);
    setCapsErr("");
    try {
      const token = await getToken();

      const payload = {};
      for (const t of tiers) {
        payload[t] = {};
        for (const k of capFields) {
          payload[t][k] = parseNonNegInt(tierCaps?.[t]?.[k] ?? 0);
        }
        for (const pk of priceFields) {
          payload[t][pk] = parseNonNegMoney(tierCaps?.[t]?.[pk] ?? 0);
        }

        // ✅ NEW: include Stripe IDs in payload
        payload[t].stripe_price_id_monthly = String(
          tierCaps?.[t]?.stripe_price_id_monthly || ""
        ).trim();
        payload[t].stripe_price_id_yearly = String(
          tierCaps?.[t]?.stripe_price_id_yearly || ""
        ).trim();
      }

      // ✅ signup bonus
      payload.signup_bonus = {
        images: parseNonNegInt(tierCaps?.signup_bonus?.images ?? 0),
        videos: parseNonNegInt(tierCaps?.signup_bonus?.videos ?? 0),
        urls: parseNonNegInt(tierCaps?.signup_bonus?.urls ?? 0),
      };

      payload.addons = {
        images_10: {
          amount: parseNonNegInt(tierCaps?.addons?.images_10?.amount ?? 0),
          price_eur: parseNonNegMoney(tierCaps?.addons?.images_10?.price_eur ?? 0),
          stripe_price_id: String(
            tierCaps?.addons?.images_10?.stripe_price_id || ""
          ).trim(),
        },
        video_1: {
          amount: parseNonNegInt(tierCaps?.addons?.video_1?.amount ?? 0),
          price_eur: parseNonNegMoney(tierCaps?.addons?.video_1?.price_eur ?? 0),
          stripe_price_id: String(
            tierCaps?.addons?.video_1?.stripe_price_id || ""
          ).trim(),
        },
        url_1: {
          amount: parseNonNegInt(tierCaps?.addons?.url_1?.amount ?? 0),
          price_eur: parseNonNegMoney(tierCaps?.addons?.url_1?.price_eur ?? 0),
          stripe_price_id: String(
            tierCaps?.addons?.url_1?.stripe_price_id || ""
          ).trim(),
        },
      };

      const res = await fetch(`${API_BASE}/api/admin/tier-caps`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to save tier caps");

      const normalized = normalizeTierCaps(json?.caps || payload);
      setTierCaps(normalized);
      setCapsDirty(false);

      // ✅ refresh validation status after save
      await validateStripePriceIds(normalized);
    } catch (e) {
      setCapsErr(e.message || "Failed to save tier caps");
    } finally {
      setCapsSaving(false);
    }
  };

  const resetTierCapsToDefaults = () => {
    setTierCaps({ ...DEFAULT_TIER_CAPS_FE });
    setCapsDirty(true);
    setStripeCheck({ prices: {}, errors: {} });
  };

  useEffect(() => {
    if (showCaps && tierCaps == null && !capsLoading) {
      loadTierCaps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaps]);

  useEffect(() => {
    if (showRetention && retentionCfg == null && !retentionLoading) {
      loadGeneratedAssetsRetention();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRetention]);

  useEffect(() => {
    if (showTopviewQueue && topviewQueueCfg == null && !topviewQueueLoading) {
      loadTopviewQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTopviewQueue]);

  useEffect(() => {
    loadTopviewKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCap = (tierKey, fieldKey, value) => {
    setTierCaps((prev) => {
      const next = { ...(prev || {}) };
      next[tierKey] = { ...(next[tierKey] || {}) };
      next[tierKey][fieldKey] = value;
      return next;
    });
    setCapsDirty(true);
  };

  const updateAddon = (addonKey, fieldKey, value) => {
    setTierCaps((prev) => {
      const next = { ...(prev || {}) };
      next.addons = { ...(next.addons || {}) };
      next.addons[addonKey] = { ...(next.addons[addonKey] || {}) };
      next.addons[addonKey][fieldKey] = value;
      return next;
    });
    setCapsDirty(true);
  };

  const updateSignupBonus = (fieldKey, value) => {
    setTierCaps((prev) => {
      const next = { ...(prev || {}) };
      next.signup_bonus = { ...(next.signup_bonus || {}) };
      next.signup_bonus[fieldKey] = value;
      return next;
    });
    setCapsDirty(true);
  };

  const ttPreview = useMemo(() => {
    if (!ttUser?.cycle_window?.start || !ttUser?.cycle_window?.end) return null;
    const currentShift = Number(ttUser.time_travel_months || 0);
    const delta = Number(ttMonths || 0) - currentShift;

    const start = new Date(ttUser.cycle_window.start);
    const end = new Date(ttUser.cycle_window.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
      return null;

    const nextStart = delta ? addMonthsSafe(start, delta) : start;
    const nextEnd = delta ? addMonthsSafe(end, delta) : end;

    return { start: nextStart, end: nextEnd };
  }, [ttUser, ttMonths]);

  // ✅ tiny input styles for table filters
  const filterInputClass =
    "w-full px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500 text-xs";
  const filterLabelClass = "text-[10px] uppercase tracking-wide text-zinc-500";

  const addonsLabel = (r) => {
    const ai = Number(r?.addon_image_count || 0);
    const av = Number(r?.addon_video_count || 0);
    const au = Number(r?.addon_url_count || 0);
    if (ai === 0 && av === 0 && au === 0) return null;
    return `Bonus +${ai} img · +${av} vid · +${au} url`;
  };

  /** ---- Mobile Card ---- */
  const UserCard = ({ r, idx }) => {
    const billingInactive = isBillingInactive(r);
    const neverPaid = isNeverPaid(r);
    const isBusyUnlimited = busyUnlimited.has(r.id);
    const isBusyBlocked = busyBlocked.has(r.id);

    return (
      <div
        className="p-3 rounded-xl border border-zinc-800/60"
        style={{ background: CARD }}
      >
        <div className="flex items-start gap-3">
          <div className="pt-0.5 text-zinc-500 text-xs shrink-0">#{idx + 1}</div>
          <Avatar name={r.name} email={r.email} />
          <div className="min-w-0 flex-1">
            <div className="text-zinc-100 font-medium truncate">
              {r.name || "—"}
            </div>
            <div className="text-zinc-400 text-xs break-all">{r.email}</div>

            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {neverPaid ? (
                <Chip>Billing inactive</Chip>
              ) : billingInactive ? (
                <>
                  <Chip kind="red">Cancelled</Chip>
                  <Chip>Billing inactive</Chip>
                </>
              ) : (
                <>
                  {r.is_paying ? (
                    <Chip kind="green">Paying</Chip>
                  ) : (
                    <Chip>Not paying</Chip>
                  )}
                  {r.current_tier ? (
                    <Chip kind="accent">{r.current_tier}</Chip>
                  ) : (
                    <Chip>no tier</Chip>
                  )}
                  <Chip kind={statusTone(r.sub_status)}>
                    {r.sub_status || "no sub"}
                  </Chip>
                  <Chip kind={r.cancel_at_period_end ? "yellow" : "green"}>
                    {renewalOrCancelCell(r)}
                  </Chip>
                </>
              )}
              {r.has_unlimited ? <Chip kind="yellow">Unlimited</Chip> : null}
              {r.is_blocked ? <Chip kind="red">Blocked</Chip> : null}
              {Number(r.time_travel_months || 0) !== 0 ? (
                <Chip kind="yellow">
                  Time travel{" "}
                  {r.time_travel_months > 0
                    ? `+${r.time_travel_months}`
                    : r.time_travel_months}
                  m
                </Chip>
              ) : null}
              {addonsLabel(r) ? (
                <Chip kind="accent" size="sm">
                  {addonsLabel(r)}
                </Chip>
              ) : null}
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <Chip size="sm">Imgs {Number(r.images_count ?? 0)}</Chip>
              <Chip size="sm">Vids {Number(r.videos_count ?? 0)}</Chip>
              <Chip size="sm">URLs {Number(r.urls_count ?? 0)}</Chip>
              <Chip size="sm" kind="yellow">
                Rev {euro((Number(r.revenue_total_cents || 0) || 0) / 100)}
              </Chip>
              <Chip size="sm" kind="accent">
                Paid {paidMonthsLabel(r)}
              </Chip>
            </div>

            <div className="mt-2 text-xs text-zinc-400">
              <div>
                Created:{" "}
                {r.created_at
                  ? new Date(r.created_at).toLocaleDateString()
                  : "—"}
              </div>
              <div>
                Last seen:{" "}
                {r.last_seen_at ? new Date(r.last_seen_at).toLocaleString() : "—"}
              </div>
              <div>Renewal/Cancels: {fmtMaybeEpochOrIso(r.current_period_end)}</div>
            </div>

            <div className="mt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <Link
                to={`/admin/users/${encodeURIComponent(r.id)}`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300 text-sm"
              >
                <ExternalLink size={14} /> View
              </Link>

              <button
                onClick={() => openAddons(r)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300 text-sm"
                aria-label="Free add-ons"
                title="Free add-ons"
              >
                <Gift size={14} /> Add-ons
              </button>

              <button
                onClick={() => openTimeTravel(r)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300 text-sm"
                aria-label="Quota time travel"
                title="Quota time travel"
              >
                <Clock size={14} /> Time travel
              </button>

              {r.has_unlimited ? (
                <button
                  disabled={isBusyUnlimited}
                  onClick={() => toggleUnlimited(r.id, false)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    isBusyUnlimited
                      ? "opacity-60 cursor-wait"
                      : "hover:border-amber-500"
                  } border-amber-600/40 text-amber-300`}
                >
                  Remove Unlimited
                </button>
              ) : (
                <button
                  disabled={isBusyUnlimited}
                  onClick={() => toggleUnlimited(r.id, true)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    isBusyUnlimited
                      ? "opacity-60 cursor-wait"
                      : "hover:border-emerald-500"
                  } border-emerald-600/40 text-emerald-300`}
                >
                  Grant Unlimited
                </button>
              )}

              {r.is_blocked ? (
                <button
                  disabled={isBusyBlocked}
                  onClick={() => toggleBlocked(r.id, false)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    isBusyBlocked
                      ? "opacity-60 cursor-wait"
                      : "hover:border-emerald-500"
                  } border-emerald-600/40 text-emerald-300`}
                >
                  Unblock
                </button>
              ) : (
                <button
                  disabled={isBusyBlocked}
                  onClick={() => toggleBlocked(r.id, true)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    isBusyBlocked
                      ? "opacity-60 cursor-wait"
                      : "hover:border-rose-500"
                  } border-rose-600/40 text-rose-300`}
                >
                  Block
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    // ✅ BG covers full viewport (no white gaps) + responsive container
    <div className="min-h-screen w-full flex flex-col" style={{ background: BG }}>
      <Header>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => navigate("/generate")}
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
              aria-label="Back to dashboard"
            >
              <ArrowLeft size={16} />
              Dashboard
            </button>

            <Link
              to="/admin/landing"
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
              aria-label="Go to Landing Settings"
              title="Go to Landing Settings"
            >
              <LayoutPanelTop size={16} />
              Landing
            </Link>

            <Link
              to="/admin/promo-modal"
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
              aria-label="Go to Promo Modal"
              title="Go to Promo Modal"
            >
              <Gift size={16} />
              Promo modal
            </Link>

            <div className="flex items-baseline gap-3">
              <h1 className="text-base sm:text-lg font-semibold text-zinc-100">
                Admin · Users
              </h1>
              <span className="text-base sm:text-lg font-semibold text-zinc-100">
                {total} users
              </span>
            </div>
          </div>

          <button
            onClick={clearColumnFilters}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 text-sm"
            title="Clear column filters"
          >
            <X size={16} />
            Clear column filters
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Chip size="lg" kind="accent">
            Tiers {euro(globalTotals.tiers_eur)}
          </Chip>
          <Chip size="lg" kind="green">
            Add-ons {euro(globalTotals.addons_eur)}
          </Chip>
          <Chip size="lg" kind="yellow">
            Total {euro(globalTotals.total_eur)}
          </Chip>
        </div>

        <AdminAlertsPanel />

        {/* Tier caps + prices + add-ons + stripe ids editor */}
        <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60">
          <button
            onClick={() => setShowCaps((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-zinc-200 hover:text-pink-300"
            aria-expanded={showCaps}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium">Tier caps</span>
              {capsDirty ? (
                <Chip kind="yellow" size="sm">
                  Unsaved
                </Chip>
              ) : null}
            </div>
            {showCaps ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showCaps ? (
            <div className="px-3 pb-3">
              {capsErr ? (
                <div className="mb-2 text-sm text-amber-300">{capsErr}</div>
              ) : null}

              {stripeCheck?.errors?._api ? (
                <div className="mb-2 text-sm text-rose-300">
                  Stripe lookup: {stripeCheck.errors._api}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 mb-2">
                <button
                  onClick={resetTierCapsToDefaults}
                  disabled={capsSaving}
                  className={`px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 ${
                    capsSaving ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  Reset
                </button>

                <button
                  onClick={saveTierCaps}
                  disabled={!capsDirty || capsSaving || !tierCaps}
                  className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                    capsSaving ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  {capsSaving ? "Saving…" : "Save caps + prices"}
                </button>

                <button
                  onClick={() => validateStripePriceIds()}
                  disabled={!tierCaps || stripeCheckBusy}
                  className={`px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 ${
                    stripeCheckBusy ? "opacity-60 cursor-wait" : ""
                  }`}
                  title="Validate Stripe price IDs"
                >
                  {stripeCheckBusy ? "Checking…" : "Check Stripe IDs"}
                </button>
              </div>

              {/* Tier caps + prices + stripe ids table */}
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm border border-zinc-800/60 rounded-lg"
                  style={{ background: CARD }}
                >
                  <thead
                    className="text-zinc-300"
                    style={{ background: "rgba(255,255,255,0.02)" }}
                  >
                    <tr>
                      <th className="text-left px-3 py-2">Tier</th>
                      <th className="text-left px-3 py-2">Images / month</th>
                      <th className="text-left px-3 py-2">Videos / month</th>
                      <th className="text-left px-3 py-2">URLs / month</th>
                      <th className="text-left px-3 py-2">Price (monthly, EUR)</th>
                      <th className="text-left px-3 py-2">Price (yearly, EUR)</th>
                      <th className="text-left px-3 py-2">Stripe ID (monthly)</th>
                      <th className="text-left px-3 py-2">Stripe ID (yearly)</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-200">
                    {tiers.map((t) => (
                      <tr key={t} className="border-t border-zinc-800/60">
                        <td className="px-3 py-2 capitalize">{t}</td>

                        {capFields.map((k) => (
                          <td key={k} className="px-3 py-2">
                            <input
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={
                                tierCaps?.[t]?.[k] === null ||
                                tierCaps?.[t]?.[k] === undefined
                                  ? ""
                                  : String(tierCaps?.[t]?.[k])
                              }
                              onChange={(e) =>
                                updateCap(t, k, parseNonNegInt(e.target.value))
                              }
                              className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                              aria-label={`${t} ${k}`}
                            />
                          </td>
                        ))}

                        {priceFields.map((pk) => (
                          <td key={pk} className="px-3 py-2">
                            <input
                              inputMode="decimal"
                              value={
                                tierCaps?.[t]?.[pk] === null ||
                                tierCaps?.[t]?.[pk] === undefined
                                  ? ""
                                  : String(tierCaps?.[t]?.[pk])
                              }
                              onChange={(e) =>
                                updateCap(t, pk, parseNonNegMoney(e.target.value))
                              }
                              className="w-28 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                              aria-label={`${t} ${pk}`}
                            />
                          </td>
                        ))}

                        <td className="px-3 py-2">
                          <input
                            value={String(tierCaps?.[t]?.stripe_price_id_monthly || "")}
                            onChange={(e) =>
                              updateCap(t, "stripe_price_id_monthly", e.target.value)
                            }
                            className="w-72 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                            aria-label={`${t} stripe monthly price id`}
                            placeholder="price_..."
                          />
                          {stripeBadgeForId(
                            tierCaps?.[t]?.stripe_price_id_monthly,
                            "month"
                          )}
                        </td>

                        <td className="px-3 py-2">
                          <input
                            value={String(tierCaps?.[t]?.stripe_price_id_yearly || "")}
                            onChange={(e) =>
                              updateCap(t, "stripe_price_id_yearly", e.target.value)
                            }
                            className="w-72 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                            aria-label={`${t} stripe yearly price id`}
                            placeholder="price_..."
                          />
                          {stripeBadgeForId(
                            tierCaps?.[t]?.stripe_price_id_yearly,
                            "year"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ✅ Signup bonus (one-time) editor */}
              <div className="mt-4">
                <div className="text-zinc-200 font-medium mb-2">
                  Signup bonus (one-time)
                </div>

                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm border border-zinc-800/60 rounded-lg"
                    style={{ background: CARD }}
                  >
                    <thead
                      className="text-zinc-300"
                      style={{ background: "rgba(255,255,255,0.02)" }}
                    >
                      <tr>
                        <th className="text-left px-3 py-2">Images</th>
                        <th className="text-left px-3 py-2">Videos</th>
                        <th className="text-left px-3 py-2">Product pages (URLs)</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-200">
                      <tr className="border-t border-zinc-800/60">
                        {["images", "videos", "urls"].map((k) => (
                          <td key={k} className="px-3 py-2">
                            <input
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={
                                tierCaps?.signup_bonus?.[k] === null ||
                                tierCaps?.signup_bonus?.[k] === undefined
                                  ? ""
                                  : String(tierCaps?.signup_bonus?.[k])
                              }
                              onChange={(e) =>
                                updateSignupBonus(k, parseNonNegInt(e.target.value))
                              }
                              className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                              aria-label={`signup bonus ${k}`}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-2 text-xs text-zinc-500">
                  These are granted once to new users when they start the free trial /
                  signup offer. Existing users are not affected.
                </div>
              </div>

              {/* Add-ons editor */}
              <div className="mt-4">
                <div className="text-zinc-200 font-medium mb-2">Add-ons</div>

                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm border border-zinc-800/60 rounded-lg"
                    style={{ background: CARD }}
                  >
                    <thead
                      className="text-zinc-300"
                      style={{ background: "rgba(255,255,255,0.02)" }}
                    >
                      <tr>
                        <th className="text-left px-3 py-2">Add-on</th>
                        <th className="text-left px-3 py-2">Amount</th>
                        <th className="text-left px-3 py-2">Price (EUR)</th>
                        <th className="text-left px-3 py-2">Stripe price ID</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-200">
                      {[
                        { key: "images_10", label: "Images pack" },
                        { key: "video_1", label: "Videos pack" },
                        { key: "url_1", label: "URLs pack" },
                      ].map((a) => (
                        <tr key={a.key} className="border-t border-zinc-800/60">
                          <td className="px-3 py-2">{a.label}</td>

                          <td className="px-3 py-2">
                            <input
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={
                                tierCaps?.addons?.[a.key]?.amount === null ||
                                tierCaps?.addons?.[a.key]?.amount === undefined
                                  ? ""
                                  : String(tierCaps?.addons?.[a.key]?.amount)
                              }
                              onChange={(e) =>
                                updateAddon(
                                  a.key,
                                  "amount",
                                  parseNonNegInt(e.target.value)
                                )
                              }
                              className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                              aria-label={`${a.key} amount`}
                            />
                          </td>

                          <td className="px-3 py-2">
                            <input
                              inputMode="decimal"
                              value={
                                tierCaps?.addons?.[a.key]?.price_eur === null ||
                                tierCaps?.addons?.[a.key]?.price_eur === undefined
                                  ? ""
                                  : String(tierCaps?.addons?.[a.key]?.price_eur)
                              }
                              onChange={(e) =>
                                updateAddon(
                                  a.key,
                                  "price_eur",
                                  parseNonNegMoney(e.target.value)
                                )
                              }
                              className="w-28 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                              aria-label={`${a.key} price`}
                            />
                          </td>

                          <td className="px-3 py-2">
                            <input
                              value={String(
                                tierCaps?.addons?.[a.key]?.stripe_price_id || ""
                              )}
                              onChange={(e) =>
                                updateAddon(a.key, "stripe_price_id", e.target.value)
                              }
                              className="w-72 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                              aria-label={`${a.key} stripe price id`}
                              placeholder="price_..."
                            />
                            {stripeBadgeForId(
                              tierCaps?.addons?.[a.key]?.stripe_price_id,
                              null
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-2 text-xs text-zinc-500">
                  Changing add-on amount changes what the customer receives.
                  Changing price changes what they pay on the Pricing page and in
                  checkout.
                </div>
              </div>

              {!tierCaps && !capsLoading ? (
                <div className="mt-2 text-sm text-zinc-400">
                  Click “Reset” to revert to defaults, then “Save caps + prices”
                  to apply.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 mt-3">
          <button
            onClick={() => setShowTopviewQueue((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-zinc-200 hover:text-pink-300"
            aria-expanded={showTopviewQueue}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium">TopView queue</span>
              {topviewQueueDirty ? (
                <Chip kind="yellow" size="sm">
                  Unsaved
                </Chip>
              ) : null}
            </div>
            {showTopviewQueue ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showTopviewQueue ? (
            <div className="px-3 pb-3">
              {topviewQueueErr ? (
                <div className="mb-2 text-sm text-amber-300">{topviewQueueErr}</div>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                  onClick={resetTopviewQueueToDefaults}
                  disabled={topviewQueueSaving}
                  className={`px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 ${
                    topviewQueueSaving ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  Reset
                </button>

                <button
                  onClick={saveTopviewQueue}
                  disabled={!topviewQueueDirty || topviewQueueSaving || !topviewQueueCfg}
                  className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                    topviewQueueSaving ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  {topviewQueueSaving ? "Saving…" : "Save queue"}
                </button>
              </div>

              {topviewQueueCfg ? (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <label className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-200">
      <div className="text-sm font-medium mb-2">Tick interval (ms)</div>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        value={
          topviewQueueCfg.tick_ms == null
            ? ""
            : String(topviewQueueCfg.tick_ms)
        }
        onChange={(e) => {
          setTopviewQueueCfg((prev) => ({
            ...(prev || {}),
            tick_ms: parseNonNegInt(e.target.value),
          }));
          setTopviewQueueDirty(true);
        }}
        className="w-full px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
      />
    </label>

    <label className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-200">
      <div className="text-sm font-medium mb-2">Jobs claimed per tick</div>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        value={
          topviewQueueCfg.batch == null
            ? ""
            : String(topviewQueueCfg.batch)
        }
        onChange={(e) => {
          setTopviewQueueCfg((prev) => ({
            ...(prev || {}),
            batch: parseNonNegInt(e.target.value),
          }));
          setTopviewQueueDirty(true);
        }}
        className="w-full px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
      />
    </label>

    <label className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-200">
      <div className="text-sm font-medium mb-2">Queue limit per API</div>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        value={
          topviewQueueCfg.per_api_limit == null
            ? ""
            : String(topviewQueueCfg.per_api_limit)
        }
        onChange={(e) => {
          setTopviewQueueCfg((prev) => ({
            ...(prev || {}),
            per_api_limit: parseNonNegInt(e.target.value),
          }));
          setTopviewQueueDirty(true);
        }}
        className="w-full px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
      />
    </label>
  </div>
) : null}

              <div className="mt-2 text-xs text-zinc-500">
                Controls the TopView worker loop. Tick interval is how often the worker checks the queue. Batch is how many queued jobs it claims per tick.
              </div>
              <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 mt-3">
  <div className="px-3 py-3">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="font-medium text-zinc-100">TopView API keys pool</div>
        <div className="text-xs text-zinc-500 mt-1">
          Manage per-key queue capacity and runtime status.
        </div>
      </div>
    </div>

    {topviewKeysErr ? (
      <div className="mt-2 text-sm text-amber-300">{topviewKeysErr}</div>
    ) : null}

    {topviewKeysLoading ? (
      <div className="mt-3 text-sm text-zinc-400">Loading TopView keys...</div>
    ) : null}

    {!topviewKeysLoading ? (
      <div className="mt-3 overflow-x-auto">
        <table
          className="w-full text-sm border border-zinc-800/60 rounded-lg"
          style={{ background: CARD }}
        >
          <thead
            className="text-zinc-300"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">UID</th>
              <th className="text-left px-3 py-2">API key</th>
              <th className="text-left px-3 py-2">Queue limit</th>
              <th className="text-left px-3 py-2">Active jobs</th>
              <th className="text-left px-3 py-2">Queue enabled</th>
              <th className="text-left px-3 py-2">Health</th>
              <th className="text-left px-3 py-2">Enabled</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>

          <tbody className="text-zinc-200">
            {topviewKeys.map((key) => (
              <tr key={key.id} className="border-t border-zinc-800/60">
                <td className="px-3 py-2">{key.label || "—"}</td>
                <td className="px-3 py-2">{key.uid || "—"}</td>
                <td className="px-3 py-2">{key.masked_api_key || "—"}</td>

                <td className="px-3 py-2">
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={String(key.concurrency_limit ?? 1)}
                    onChange={(e) => {
                      const nextVal = Math.max(
                        1,
                        parseNonNegInt(e.target.value || 1)
                      );
                      setTopviewKeys((prev) =>
                        prev.map((k) =>
                          k.id === key.id
                            ? {
                                ...k,
                                concurrency_limit: nextVal,
                              }
                            : k
                        )
                      );
                    }}
                    className="w-20 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                  />
                </td>

                <td className="px-3 py-2">{Number(key.active_jobs || 0)}</td>

                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={key.runtime_enabled !== false}
                    onChange={(e) => {
                      setTopviewKeys((prev) =>
                        prev.map((k) =>
                          k.id === key.id
                            ? {
                                ...k,
                                runtime_enabled: e.target.checked,
                                enabled: e.target.checked,
                              }
                            : k
                        )
                      );
                    }}
                    className="h-4 w-4 accent-pink-500"
                  />
                </td>

                <td className="px-3 py-2">{key.health_status || "healthy"}</td>

                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={key.enabled !== false}
                    onChange={(e) => {
                      setTopviewKeys((prev) =>
                        prev.map((k) =>
                          k.id === key.id
                            ? {
                                ...k,
                                enabled: e.target.checked,
                                runtime_enabled: e.target.checked,
                              }
                            : k
                        )
                      );
                    }}
                    className="h-4 w-4 accent-pink-500"
                  />
                </td>

                <td className="px-3 py-2">
                  <button
                    onClick={() => saveTopviewKeyQueue(key.id)}
                    disabled={busyTopviewKeySave.has(key.id)}
                    className={`px-3 py-1 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                      busyTopviewKeySave.has(key.id)
                        ? "opacity-60 cursor-wait"
                        : ""
                    }`}
                  >
                    {busyTopviewKeySave.has(key.id) ? "Saving..." : "Save queue"}
                  </button>
                </td>
              </tr>
            ))}

            {!topviewKeys.length ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-zinc-500">
                  No TopView keys found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    ) : null}
  </div>
</div>
            </div>
          ) : null}
        </div>

        <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 mt-3">
          <button
            onClick={() => setShowRetention((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-zinc-200 hover:text-pink-300"
            aria-expanded={showRetention}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium">Generated assets retention</span>
              {retentionDirty ? (
                <Chip kind="yellow" size="sm">
                  Unsaved
                </Chip>
              ) : null}
            </div>
            {showRetention ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showRetention ? (
            <div className="px-3 pb-3">
              {retentionErr ? (
                <div className="mb-2 text-sm text-amber-300">{retentionErr}</div>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                  onClick={resetGeneratedAssetsRetentionToDefaults}
                  disabled={retentionSaving || retentionRunning}
                  className={`px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 ${
                    retentionSaving || retentionRunning ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  Reset
                </button>

                <button
                  onClick={saveGeneratedAssetsRetention}
                  disabled={!retentionDirty || retentionSaving || retentionRunning || !retentionCfg}
                  className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                    retentionSaving || retentionRunning ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  {retentionSaving ? "Saving…" : "Save retention"}
                </button>

                <button
                  onClick={runGeneratedAssetsRetentionNow}
                  disabled={retentionSaving || retentionRunning}
                  className={`px-3 py-1.5 rounded-md border border-amber-700/50 text-amber-300 hover:border-amber-500 ${
                    retentionSaving || retentionRunning ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  {retentionRunning ? "Running…" : "Run now"}
                </button>
              </div>

              {retentionCfg ? (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
    <label className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-200 flex items-center justify-between gap-3">
      <span className="text-sm font-medium">Enabled</span>
      <input
        type="checkbox"
        checked={retentionCfg.enabled !== false}
        onChange={(e) => {
          setRetentionCfg((prev) => ({
            ...(prev || {}),
            enabled: e.target.checked,
          }));
          setRetentionDirty(true);
        }}
        className="h-4 w-4 accent-pink-500"
      />
    </label>

    <label className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-200">
      <div className="text-sm font-medium mb-2">Retention days</div>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        value={
          retentionCfg.retention_days == null
            ? ""
            : String(retentionCfg.retention_days)
        }
        onChange={(e) => {
          setRetentionCfg((prev) => ({
            ...(prev || {}),
            retention_days: parseNonNegInt(e.target.value),
          }));
          setRetentionDirty(true);
        }}
        className="w-full px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
      />
    </label>

    <label className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-200">
      <div className="text-sm font-medium mb-2">Run frequency</div>
      <select
        value={String(retentionCfg.frequency || "daily")}
        onChange={(e) => {
          setRetentionCfg((prev) => ({
            ...(prev || {}),
            frequency: e.target.value,
          }));
          setRetentionDirty(true);
        }}
        className="w-full px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
      >
        {RETENTION_FREQUENCY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  </div>
) : null}

              {retentionCfg ? (
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-300">
                    <div className="text-zinc-100 font-medium mb-1">Schedule status</div>
                    <div>Schedule: {retentionFrequencyLabel(retentionCfg.frequency)}</div>
                    <div>Running: {retentionCfg.running ? "Yes" : "No"}</div>
                    <div>Last run: {fmtMaybeEpochOrIso(retentionCfg.last_run_at)}</div>
                  </div>

                  <div className="rounded-lg border border-zinc-800/60 p-3 bg-zinc-950/40 text-zinc-300 lg:col-span-2">
                    <div className="text-zinc-100 font-medium mb-1">Last cleanup result</div>
                    {retentionCfg.last_result ? (
                      <div className="space-y-1">
                        <div>
                          Deleted DB rows: {Number(retentionCfg.last_result.deleted_db || 0)} · Deleted from Cloudinary: {Number(retentionCfg.last_result.deleted_cloudinary || 0)}
                        </div>
                        <div>
                          Missing public IDs: {Number(retentionCfg.last_result.cloudinary_missing_public_id || 0)} · Not found on Cloudinary: {Number(retentionCfg.last_result.cloudinary_not_found || 0)}
                        </div>
                        <div>
                          Eligible: {Number(retentionCfg.last_result.eligible || 0)} · Scanned: {Number(retentionCfg.last_result.scanned || 0)}
                        </div>
                        {Array.isArray(retentionCfg.last_result.errors) && retentionCfg.last_result.errors.length ? (
                          <div className="text-amber-300">
                            Errors: {retentionCfg.last_result.errors.length}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="text-zinc-500">No runs yet.</div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="mt-2 text-xs text-zinc-500">
                Applies only to generated assets. Input, analyzed, imported, and manual-upload assets are excluded. Expiration is based on asset created_at.
              </div>
            </div>
          ) : null}
        </div>

        {/* Search & Filters */}
        <div className="w-full rounded-xl border border-zinc-700/60 bg-zinc-900/60 p-2 sm:p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 flex-wrap">
            <div className="relative w-full sm:w-[300px]">
              <Search size={16} className="absolute left-2 top-2.5 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or email…"
                className="w-full pl-8 pr-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
              />
            </div>

            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col">
                <span className={filterLabelClass}>Paying</span>
                <select
                  value={paying}
                  onChange={(e) => setPaying(e.target.value)}
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                >
                  <option value="">All</option>
                  <option value="true">Paying</option>
                  <option value="false">Not paying</option>
                </select>
              </div>

              <div className="flex flex-col">
                <span className={filterLabelClass}>Cancelled</span>
                <select
                  value={canceled}
                  onChange={(e) => setCanceled(e.target.value)}
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                >
                  <option value="">All</option>
                  <option value="true">Only cancelled</option>
                  <option value="false">Exclude cancelled</option>
                </select>
              </div>

              <div className="flex flex-col">
                <span className={filterLabelClass}>Tier</span>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                >
                  <option value="">All</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                </select>
              </div>

              <div className="flex flex-col">
                <span className={filterLabelClass}>Unlimited</span>
                <select
                  value={unlimited}
                  onChange={(e) => setUnlimited(e.target.value)}
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                >
                  <option value="">All</option>
                  <option value="true">Only unlimited</option>
                  <option value="false">Exclude unlimited</option>
                </select>
              </div>

              <div className="flex flex-col">
                <span className={filterLabelClass}>Blocked</span>
                <select
                  value={blocked}
                  onChange={(e) => setBlocked(e.target.value)}
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                >
                  <option value="">All</option>
                  <option value="true">Only blocked</option>
                  <option value="false">Exclude blocked</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </Header>

      {/* ✅ Page content: whole page scrolls (mobile friendly) */}
      <div className="flex-1 px-4 py-4">
        {err ? <div className="mb-3 text-sm text-amber-300">{err}</div> : null}

        {/* Mobile */}
        <div className="grid gap-3 md:hidden">
          {rows.map((r, idx) => (
            <UserCard key={r.id} r={r} idx={idx} />
          ))}
          {!loading && rows.length === 0 ? (
            <div
              className="px-3 py-8 text-center text-zinc-400 rounded-xl border border-zinc-800/60"
              style={{ background: CARD }}
            >
              No users found.
            </div>
          ) : null}
        </div>

        {/* Desktop */}
        <div
          className="rounded-xl hidden md:block"
          style={{ background: CARD, border: `1px solid ${BORDER}` }}
        >
          <div className="overflow-x-auto">
            <table className="min-w-[1500px] w-full text-sm">
              <thead
                className="text-zinc-300"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                }}
              >
                <tr>
                  <th className="text-left px-3 py-2 w-10">#</th>

                  <th className="text-left px-3 py-2 w-[120px]">
                    <SortBtn col="created_at" label="Created" />
                  </th>
                  <th className="text-left px-3 py-2 min-w-[260px]">
                    <SortBtn col="name" label="User" />
                  </th>
                  <th className="text-left px-3 py-2 min-w-[260px]">
                    <SortBtn col="sub_status" label="Billing" />
                  </th>

                  <th className="text-left px-3 py-2 min-w-[170px]">
                    <SortBtn col="paid_months_count" label="Paid months" />
                  </th>

                  <th className="text-left px-3 py-2 min-w-[140px]">
                    <SortBtn col="images_count" label="Images created" />
                  </th>
                  <th className="text-left px-3 py-2 min-w-[140px]">
                    <SortBtn col="videos_count" label="Videos created" />
                  </th>
                  <th className="text-left px-3 py-2 min-w-[140px]">
                    <SortBtn col="urls_count" label="URLs analyzed" />
                  </th>

                  <th className="text-left px-3 py-2 min-w-[220px]">
                    <SortBtn col="revenue_total_cents" label="Revenue" />
                  </th>

                  <th className="text-left px-3 py-2 min-w-[210px]">
                    <SortBtn col="last_seen_at" label="Last seen" />
                  </th>
                  <th className="text-left px-3 py-2 min-w-[220px]">
                    <SortBtn col="current_period_end" label="Renewal/Cancels" />
                  </th>

                  <th
                    className="text-center px-3 py-2 w-[210px]"
                    style={{
                      position: "sticky",
                      right: 0,
                      background: CARD,
                      zIndex: 4,
                      boxShadow: "-8px 0 8px rgba(0,0,0,0.25)",
                    }}
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-zinc-300">Actions</span>
                    </div>
                  </th>
                </tr>

                <tr style={{ background: "rgba(0,0,0,0.18)" }}>
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2" />

                  <th className="px-3 py-2">
                    <input
                      inputMode="numeric"
                      placeholder="≥"
                      value={colFilters.paidMonths}
                      onChange={(e) => setFilter("paidMonths", e.target.value)}
                      className={filterInputClass}
                    />
                  </th>

                  <th className="px-3 py-2">
                    <input
                      inputMode="numeric"
                      placeholder="≥"
                      value={colFilters.images}
                      onChange={(e) => setFilter("images", e.target.value)}
                      className={filterInputClass}
                    />
                  </th>

                  <th className="px-3 py-2">
                    <input
                      inputMode="numeric"
                      placeholder="≥"
                      value={colFilters.videos}
                      onChange={(e) => setFilter("videos", e.target.value)}
                      className={filterInputClass}
                    />
                  </th>

                  <th className="px-3 py-2">
                    <input
                      inputMode="numeric"
                      placeholder="≥"
                      value={colFilters.urls}
                      onChange={(e) => setFilter("urls", e.target.value)}
                      className={filterInputClass}
                    />
                  </th>

                  <th className="px-3 py-2">
                    <input
                      inputMode="decimal"
                      placeholder="≥ €"
                      value={colFilters.revenue}
                      onChange={(e) => setFilter("revenue", e.target.value)}
                      className={filterInputClass}
                    />
                  </th>

                  <th className="px-3 py-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={colFilters.lastSeenAfter}
                        onChange={(e) =>
                          setFilter("lastSeenAfter", e.target.value)
                        }
                        className={filterInputClass}
                        title="Last seen after"
                      />
                      <input
                        type="date"
                        value={colFilters.lastSeenBefore}
                        onChange={(e) =>
                          setFilter("lastSeenBefore", e.target.value)
                        }
                        className={filterInputClass}
                        title="Last seen before"
                      />
                    </div>
                  </th>

                  <th className="px-3 py-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={colFilters.periodAfter}
                        onChange={(e) =>
                          setFilter("periodAfter", e.target.value)
                        }
                        className={filterInputClass}
                        title="Period end after"
                      />
                      <input
                        type="date"
                        value={colFilters.periodBefore}
                        onChange={(e) =>
                          setFilter("periodBefore", e.target.value)
                        }
                        className={filterInputClass}
                        title="Period end before"
                      />
                    </div>
                    <div className="mt-2">
                      <select
                        value={colFilters.renewalType}
                        onChange={(e) =>
                          setFilter("renewalType", e.target.value)
                        }
                        className="w-full px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
                      >
                        <option value="">Any</option>
                        <option value="renewing">Renewing</option>
                        <option value="canceling">Canceling</option>
                      </select>
                    </div>
                  </th>

                  <th
                    className="px-3 py-2 text-center"
                    style={{
                      position: "sticky",
                      right: 0,
                      background: CARD,
                      zIndex: 4,
                      boxShadow: "-8px 0 8px rgba(0,0,0,0.25)",
                    }}
                  >
                    <div className="flex items-center justify-center text-xs text-zinc-500">
                      —
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody className="text-zinc-200">
                {rows.map((r, idx) => {
                  const billingInactive = isBillingInactive(r);
                  const subTone = statusTone(r.sub_status);
                  const rowNumber = idx + 1;
                  const neverPaid = isNeverPaid(r);
                  const isBusyUnlimited = busyUnlimited.has(r.id);
                  const isBusyBlocked = busyBlocked.has(r.id);

                  return (
                    <tr key={r.id} className="border-t border-zinc-800/60">
                      <td className="px-3 py-2 text-zinc-500">{rowNumber}</td>

                      <td className="px-3 py-2 text-zinc-400">
                        {r.created_at
                          ? new Date(r.created_at).toLocaleDateString()
                          : "—"}
                      </td>

                      <td className="px-3 py-2">
                        <Link
                          to={`/admin/users/${encodeURIComponent(r.id)}`}
                          className="flex items-center gap-3 hover:underline"
                        >
                          <Avatar name={r.name} email={r.email} />
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium truncate">
                              {r.name || "—"}
                            </span>
                            <span className="text-xs text-zinc-400 break-all">
                              {r.email}
                            </span>
                          </div>
                        </Link>

                        {Number(r.time_travel_months || 0) !== 0 ? (
                          <div className="mt-1">
                            <Chip kind="yellow" size="sm">
                              Time travel{" "}
                              {r.time_travel_months > 0
                                ? `+${r.time_travel_months}`
                                : r.time_travel_months}
                              m
                            </Chip>
                          </div>
                        ) : null}

                        {addonsLabel(r) ? (
                          <div className="mt-1">
                            <Chip kind="accent" size="sm">
                              {addonsLabel(r)}
                            </Chip>
                          </div>
                        ) : null}
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {neverPaid ? (
                            <Chip>Billing inactive</Chip>
                          ) : billingInactive ? (
                            <>
                              <Chip kind="red">Cancelled</Chip>
                              <Chip>Billing inactive</Chip>
                            </>
                          ) : (
                            <>
                              {r.is_paying ? (
                                <Chip kind="green">Paying</Chip>
                              ) : (
                                <Chip>Not paying</Chip>
                              )}
                              {r.current_tier ? (
                                <Chip kind="accent">{r.current_tier}</Chip>
                              ) : (
                                <Chip>no tier</Chip>
                              )}
                              <Chip kind={subTone}>
                                {r.sub_status || "no sub"}
                              </Chip>
                              <Chip
                                kind={r.cancel_at_period_end ? "yellow" : "green"}
                              >
                                {renewalOrCancelCell(r)}
                              </Chip>
                            </>
                          )}
                          {r.has_unlimited ? (
                            <Chip kind="yellow">Unlimited</Chip>
                          ) : null}
                          {r.is_blocked ? <Chip kind="red">Blocked</Chip> : null}
                        </div>
                      </td>

                      <td className="px-3 py-2 text-zinc-100">
                        {paidMonthsLabel(r)}
                      </td>

                      <td className="px-3 py-2">{Number(r.images_count ?? 0)}</td>
                      <td className="px-3 py-2">{Number(r.videos_count ?? 0)}</td>
                      <td className="px-3 py-2">{Number(r.urls_count ?? 0)}</td>

                      <td className="px-3 py-2">{revenueCell(r)}</td>

                      <td className="px-3 py-2 text-zinc-400">
                        {r.last_seen_at
                          ? new Date(r.last_seen_at).toLocaleString()
                          : "—"}
                      </td>

                      <td className="px-3 py-2 text-zinc-400">
                        {fmtMaybeEpochOrIso(r.current_period_end)}
                      </td>

                      <td
                        className="px-3 py-2"
                        style={{
                          position: "sticky",
                          right: 0,
                          background: CARD,
                          zIndex: 4,
                          boxShadow: "-8px 0 8px rgba(0,0,0,0.25)",
                        }}
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => openAddons(r)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-zinc-700/60 hover:border-pink-600/60 text-zinc-200 hover:text-pink-300"
                            aria-label="Free add-ons"
                            title="Free add-ons"
                          >
                            <Gift size={16} />
                          </button>

                          <button
                            onClick={() => openTimeTravel(r)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-zinc-700/60 hover:border-pink-600/60 text-zinc-200 hover:text-pink-300"
                            aria-label="Quota time travel"
                            title="Quota time travel"
                          >
                            <Clock size={16} />
                          </button>

                          <button
                            disabled={isBusyUnlimited}
                            onClick={() => toggleUnlimited(r.id, !r.has_unlimited)}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${
                              isBusyUnlimited
                                ? "opacity-60 cursor-wait"
                                : "hover:border-amber-500"
                            } ${
                              r.has_unlimited
                                ? "border-amber-600/40 text-amber-300"
                                : "border-emerald-600/40 text-emerald-300"
                            }`}
                            aria-label={
                              r.has_unlimited ? "Remove Unlimited" : "Grant Unlimited"
                            }
                            title={
                              r.has_unlimited ? "Remove Unlimited" : "Grant Unlimited"
                            }
                          >
                            <InfinityIcon size={16} />
                          </button>

                          <button
                            disabled={isBusyBlocked}
                            onClick={() => toggleBlocked(r.id, !r.is_blocked)}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${
                              isBusyBlocked
                                ? "opacity-60 cursor-wait"
                                : r.is_blocked
                                ? "hover:border-emerald-500"
                                : "hover:border-rose-500"
                            } ${
                              r.is_blocked
                                ? "border-emerald-600/40 text-emerald-300"
                                : "border-rose-600/40 text-rose-300"
                            }`}
                            aria-label={r.is_blocked ? "Unblock user" : "Block user"}
                            title={r.is_blocked ? "Unblock user" : "Block user"}
                          >
                            {r.is_blocked ? <Unlock size={16} /> : <Lock size={16} />}
                          </button>

                          <Link
                            to={`/admin/users/${encodeURIComponent(r.id)}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-zinc-700/60 hover:border-pink-600/60 text-zinc-200 hover:text-pink-300"
                            aria-label="View user"
                            title="View user"
                          >
                            <ExternalLink size={16} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-3 py-8 text-center text-zinc-400"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {/* Single sentinel */}
        <div id="infinite-sentinel" style={{ height: 1 }} />

        {loading ? <div className="mt-3 text-sm text-zinc-400">Loading…</div> : null}
      </div>

      {/* ✅ Add-ons modal */}
      {addonsOpen && addonsUser ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAddons();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-zinc-700/60 bg-zinc-950/80 backdrop-blur p-4"
            style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-zinc-100 font-semibold text-lg">
                  Free add-ons
                </div>
                <div className="text-zinc-400 text-sm break-all">
                  {addonsUser.email || addonsUser.id}
                </div>
                <div className="mt-2 text-xs text-zinc-400">
                  Grant or remove free add-ons (same as paid add-ons). These
                  increase/decrease the user’s caps.
                </div>
              </div>
              <button
                onClick={closeAddons}
                className="w-9 h-9 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 text-zinc-200 hover:text-pink-300"
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="mt-3 rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-3 text-sm">
              <div className="text-zinc-300">
                Current bonus:{" "}
                <span className="text-zinc-400">
                  +{Number(addonsUser.addon_image_count || 0)} img · +
                  {Number(addonsUser.addon_video_count || 0)} vid · +
                  {Number(addonsUser.addon_url_count || 0)} url
                </span>
              </div>
              {addonsUser?.remaining_in_cycle ? (
                <div className="mt-1 text-zinc-300">
                  Remaining (cycle):{" "}
                  <span className="text-zinc-400">
                    {addonsUser.remaining_in_cycle.images === null
                      ? "∞"
                      : `${addonsUser.remaining_in_cycle.images} img`}{" "}
                    ·{" "}
                    {addonsUser.remaining_in_cycle.videos === null
                      ? "∞"
                      : `${addonsUser.remaining_in_cycle.videos} vid`}{" "}
                    ·{" "}
                    {addonsUser.remaining_in_cycle.urls === null
                      ? "∞"
                      : `${addonsUser.remaining_in_cycle.urls} url`}
                  </span>
                </div>
              ) : null}
            </div>

            {addonsErr ? (
              <div className="mt-3 text-sm text-amber-300">{addonsErr}</div>
            ) : null}

            <div className="mt-4 grid gap-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">
                    Images
                  </label>
                  <input
                    inputMode="numeric"
                    value={String(addonsForm.images)}
                    onChange={(e) =>
                      setAddonsForm((p) => ({
                        ...p,
                        images: parseNonNegInt(e.target.value),
                      }))
                    }
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">
                    Videos
                  </label>
                  <input
                    inputMode="numeric"
                    value={String(addonsForm.videos)}
                    onChange={(e) =>
                      setAddonsForm((p) => ({
                        ...p,
                        videos: parseNonNegInt(e.target.value),
                      }))
                    }
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500">
                    URLs
                  </label>
                  <input
                    inputMode="numeric"
                    value={String(addonsForm.urls)}
                    onChange={(e) =>
                      setAddonsForm((p) => ({
                        ...p,
                        urls: parseNonNegInt(e.target.value),
                      }))
                    }
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                  />
                </div>
              </div>

              <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <button
                  onClick={() => applyAddons(addonsUser.id, "remove")}
                  disabled={busyAddons.has(addonsUser.id)}
                  className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-rose-700/50 text-rose-300 hover:border-rose-500 ${
                    busyAddons.has(addonsUser.id) ? "opacity-60 cursor-wait" : ""
                  }`}
                  title="Remove these add-ons"
                >
                  Remove
                </button>

                <button
                  onClick={() => applyAddons(addonsUser.id, "grant")}
                  disabled={busyAddons.has(addonsUser.id)}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                    busyAddons.has(addonsUser.id) ? "opacity-60 cursor-wait" : ""
                  }`}
                  title="Grant these add-ons"
                >
                  Grant
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Time travel modal (kept as-is) */}
      {ttOpen && ttUser ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeTimeTravel();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-zinc-700/60 bg-zinc-950/80 backdrop-blur p-4"
            style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-zinc-100 font-semibold text-lg">
                  Quota time travel
                </div>
                <div className="text-zinc-400 text-sm break-all">
                  {ttUser.email || ttUser.id}
                </div>
                <div className="mt-2 text-xs text-zinc-400">
                  Shifts this user’s cycle window by X months (for testing resets).
                </div>
              </div>
              <button
                onClick={closeTimeTravel}
                className="w-9 h-9 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 text-zinc-200 hover:text-pink-300"
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="mt-3 rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-3 text-sm">
              <div className="text-zinc-300">
                Current window:{" "}
                <span className="text-zinc-400">
                  {fmtShort(ttUser?.cycle_window?.start)} →{" "}
                  {fmtShort(ttUser?.cycle_window?.end)}
                </span>
              </div>
              <div className="mt-1 text-zinc-300">
                Current shift:{" "}
                <span className="text-zinc-400">
                  {Number(ttUser.time_travel_months || 0) === 0
                    ? "off"
                    : `${ttUser.time_travel_months > 0 ? "+" : ""}${
                        ttUser.time_travel_months
                      } months`}
                </span>
              </div>

              {ttPreview ? (
                <div className="mt-2 text-zinc-300">
                  Preview window:{" "}
                  <span className="text-zinc-400">
                    {ttPreview.start.toLocaleDateString()} →{" "}
                    {ttPreview.end.toLocaleDateString()}
                  </span>
                </div>
              ) : null}
            </div>

            {ttErr ? (
              <div className="mt-3 text-sm text-amber-300">{ttErr}</div>
            ) : null}

            <div className="mt-4 grid gap-2">
              <label className="text-xs uppercase tracking-wide text-zinc-500">
                Shift months (−24 … +24)
              </label>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  inputMode="numeric"
                  value={String(ttMonths)}
                  onChange={(e) => {
                    const v = Number.parseInt(e.target.value, 10);
                    setTTMonths(Number.isFinite(v) ? v : 0);
                  }}
                  className="w-full sm:w-28 px-3 py-2 rounded-lg bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 focus:outline-none focus:ring-1 focus:ring-pink-500"
                />

                <div className="flex flex-wrap gap-2">
                  {[-2, -1, 0, 1, 2].map((m) => (
                    <button
                      key={m}
                      onClick={() => setTTMonths(m)}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        ttMonths === m
                          ? "border-pink-600/60 text-pink-300"
                          : "border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
                      }`}
                    >
                      {m === 0 ? "0" : m > 0 ? `+${m}` : `${m}`}m
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <button
                  onClick={() => applyTimeTravel(ttUser.id, 0)}
                  disabled={busyTimeTravel.has(ttUser.id)}
                  className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-amber-500/60 hover:text-amber-300 ${
                    busyTimeTravel.has(ttUser.id) ? "opacity-60 cursor-wait" : ""
                  }`}
                  title="Reset back to real billing cycle"
                >
                  <RotateCcw size={16} />
                  Reset
                </button>

                <button
                  onClick={() => applyTimeTravel(ttUser.id, ttMonths)}
                  disabled={busyTimeTravel.has(ttUser.id)}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                    busyTimeTravel.has(ttUser.id) ? "opacity-60 cursor-wait" : ""
                  }`}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
